import * as turf from "@turf/turf";

/**
 * Does the area brush (a GeoJSON circle polygon) touch this carrier route?
 *
 * Until 2026-09-17 a route counted as "in the area" only when the brush
 * covered more than 30% of the route's surface, which is why sweeping the
 * brush over a block visibly skipped the routes at its edge. The product rule
 * now is the obvious one: any route the brush touches is selected — the user
 * shrinks the radius when they want fewer.
 *
 * DATA SHAPE — this is the part that bit once: the map stores every route's
 * `routeGeometry` as `turf.polygon(paths)`, so `featureCoordinates` is always
 * a flat LIST OF RINGS (positions arrays), never a nested MultiPolygon. For a
 * MultiPolygon route each ring is one part; for a Polygon route the first
 * ring is the outline (any others are holes, which lie inside it). So the
 * check is per ring: the brush touches the route if it touches any ring taken
 * as its own polygon — exactly how the old area rule walked them. Building a
 * `turf.multiPolygon` from this shape produced an invalid geometry and
 * silently selected nothing (prod, 2026-09-17).
 *
 * `isMultiPolygon` is kept for call-site compatibility; both shapes are
 * handled the same way. Rings that are not closed are closed. Invalid
 * geometry never throws, it just does not select.
 */
export const routeTouchesBrush = (featureCoordinates, brushGeoJson, isMultiPolygon = false) => {
  void isMultiPolygon;
  if (!Array.isArray(featureCoordinates) || !brushGeoJson) return false;
  return featureCoordinates.some((ring) => {
    try {
      const closed = closeRing(ring);
      if (closed.length < 4) return false;
      return Boolean(turf.booleanIntersects(brushGeoJson, turf.polygon([closed])));
    } catch (e) {
      return false;
    }
  });
};

const closeRing = (ring) => {
  if (!Array.isArray(ring) || ring.length === 0) return [];
  const closed = [...ring];
  const [first] = closed;
  const last = closed[closed.length - 1];
  if (!Array.isArray(first) || !Array.isArray(last)) return [];
  if (first[0] !== last[0] || first[1] !== last[1]) closed.push(first);
  return closed;
};
